"use client";

import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";

const initialState = { name: "", email: "", company: "", message: "" };

export default function ContactForm() {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  function validate() {
    const next = {};
    if (!values.name.trim()) next.name = "Name is required.";
    if (!values.email.trim()) next.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(values.email)) next.email = "Enter a valid email address.";
    if (!values.message.trim()) next.message = "Tell us a little about what you need.";
    return next;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length === 0) {
      setSubmitted(true);
      setValues(initialState);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-3xl border border-hairline bg-white p-12 text-center">
        <CheckCircle2 className="h-10 w-10 text-primary" />
        <h3 className="text-lg font-semibold text-ink">Thanks — we got it.</h3>
        <p className="max-w-xs text-sm text-muted">
          A member of our team will reach out within one business day to schedule your demo.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="focus-ring mt-2 text-sm font-semibold text-primary"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-3xl border border-hairline bg-white p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full name" name="name" value={values.name} onChange={handleChange} error={errors.name} />
        <Field
          label="Work email"
          name="email"
          type="email"
          value={values.email}
          onChange={handleChange}
          error={errors.email}
        />
      </div>
      <div className="mt-5">
        <Field label="Company (optional)" name="company" value={values.company} onChange={handleChange} />
      </div>
      <div className="mt-5">
        <label htmlFor="message" className="mb-2 block text-sm font-medium text-ink">
          How can we help?
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          value={values.message}
          onChange={handleChange}
          placeholder="Tell us about your team size and what you're hoping to solve..."
          className={`rounded rounded-2xl w-full border bg-white px-4 py-3 text-sm text-ink placeholder:text-muted ${
            errors.message ? "border-red-400" : "border-hairline"
          }`}
        />
        {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
      </div>
      <button type="submit" className="btn-primary mt-7 w-full sm:w-auto">
        Send message <Send className="h-4 w-4" />
      </button>
    </form>
  );
}

function Field({ label, name, type = "text", value, onChange, error }) {
  return (
    <div>
      <label htmlFor={name} className="mb-2 block text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className={`rounded w-full rounded-2xl border bg-white px-4 py-3 text-sm text-ink placeholder:text-muted ${
          error ? "border-red-400" : "border-hairline"
        }`}
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
